import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import AdminShell from "./AdminShell";
import { getDataAPIAuth, postDataAPI, putDataAPI } from "../../utils/API";
import { dialog } from "../Common/TreasuryDialog";

const GOLD = "#f8b602";
const GREEN = "#0e3725";
const API = process.env.REACT_APP_API_URL || "";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const colors = {
    active: { bg: "#d1fae5", color: "#065f46" },
    pending: { bg: "#fef3c7", color: "#92400e" },
    inactive: { bg: "#f3f4f6", color: "#6b7280" },
    failed: { bg: "#fee2e2", color: "#991b1b" },
    received: { bg: "#dbeafe", color: "#1e40af" },
    verified: { bg: "#e0e7ff", color: "#3730a3" },
    credited: { bg: "#d1fae5", color: "#065f46" },
    rejected: { bg: "#fee2e2", color: "#991b1b" },
    duplicate: { bg: "#f3f4f6", color: "#6b7280" },
  };
  const s = colors[status] || { bg: "#f3f4f6", color: "#6b7280" };
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px",
      borderRadius: 12, fontSize: 11, fontWeight: 700,
      background: s.bg, color: s.color, textTransform: "uppercase",
    }}>{status}</span>
  );
};

const SAR = (amount) =>
  new Intl.NumberFormat("en-SA", { style: "currency", currency: "SAR", maximumFractionDigits: 2 }).format(amount || 0);

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-SA", { year: "numeric", month: "short", day: "numeric" }) : "—";

// ─── Tab: Config ──────────────────────────────────────────────────────────────
const ConfigTab = ({ token }) => {
  const [config, setConfig] = useState({
    clientId: "", clientSecret: "", partnerId: "",
    merchantId: "", serviceId: "", baseUrl: "https://staging.api.alinma.com",
    collectionAccount: "", collectionIban: "",
    tlsPrivateKey: "", signingPrivateKey: "", consumerSystemName: "treasury-platform",
    isActive: false, webhookSecret: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await getDataAPIAuth("admin/alinma/config", token);
      if (res.data.status === 1) setConfig(c => ({ ...c, ...res.data.data }));
    } catch (e) {}
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await putDataAPI("admin/alinma/config", config, token);
      if (res.data.status === 1) {
        dialog.alert("Saved", "Configuration saved successfully.");
        load();
      }
    } catch (e) {
      dialog.alert("Error", e.response?.data?.msg || e.message);
    }
    setSaving(false);
  };

  const testConn = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await postDataAPI("admin/alinma/test-connection", {}, token, 90000);
      setTestResult({ ok: true, msg: res.data.msg, steps: res.data.steps || [] });
    } catch (e) {
      setTestResult({ ok: false, msg: e.response?.data?.msg || e.message || "Connection failed", steps: e.response?.data?.steps || [] });
    }
    setTesting(false);
  };

  if (loading) return <p style={{ color: "#6b7280", padding: 24 }}>Loading configuration…</p>;

  const field = (label, key, type = "text", placeholder = "") => (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>{label}</label>
      <input
        type={type}
        value={config[key] || ""}
        onChange={e => setConfig(c => ({ ...c, [key]: e.target.value }))}
        placeholder={placeholder || label}
        style={{
          width: "100%", padding: "10px 14px", border: "1px solid #d1d5db",
          borderRadius: 8, fontSize: 13, color: "#111827",
          background: "#fff", outline: "none", boxSizing: "border-box",
        }}
      />
    </div>
  );

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={{ background: "#fff", borderRadius: 12, padding: 24, border: "1px solid #e5e7eb" }}>
          <h3 style={{ margin: "0 0 20px", fontSize: 15, fontWeight: 700, color: GREEN }}>API Credentials</h3>
          {field("Client ID (API Key)", "clientId", "text", "API Key from the treasury app on developer.alinma.com")}
          {field("Client Secret (API Secret)", "clientSecret", "password", "API Secret from the treasury app")}
          {field("Merchant ID", "merchantId", "text", "Merchant Identifier from Alinma onboarding (e.g. 19998)")}
          {field("Account Service ID", "serviceId", "text", "accountServiceId from Alinma onboarding (e.g. 100)")}
          {field("Partner ID", "partnerId", "text", "Alinma partner number; mapped to our app by Alinma (not sent in v2 calls)")}
          {field("Collection Account", "collectionAccount", "text", "Treasury's Alinma account the virtual IBANs settle into")}
          {field("Collection IBAN", "collectionIban", "text", "IBAN of the collection account")}
          {field("Base URL", "baseUrl", "text", "https://staging.api.alinma.com")}
          {field("Consumer System Name", "consumerSystemName", "text", "x-consumer-system-name header (default: treasury-platform)")}
        </div>
        <div>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, border: "1px solid #e5e7eb", marginBottom: 20 }}>
            <h3 style={{ margin: "0 0 20px", fontSize: 15, fontWeight: 700, color: GREEN }}>Webhook</h3>
            {field("Webhook Secret", "webhookSecret", "password", "HMAC-SHA256 signing secret")}
            <div style={{ background: "#f0fdf4", borderRadius: 8, padding: 12, fontSize: 12, color: "#166534" }}>
              <strong>Webhook URL:</strong><br />
              <code style={{ wordBreak: "break-all" }}>
                {window.location.origin}/api/alinma/webhook
              </code>
            </div>
          </div>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, border: "1px solid #e5e7eb", marginBottom: 20 }}>
            <h3 style={{ margin: "0 0 20px", fontSize: 15, fontWeight: 700, color: GREEN }}>Security Material (v2.0)</h3>
            <p style={{ margin: "0 0 14px", fontSize: 12, color: "#6b7280" }}>
              The v2.0 API enforces mutual TLS and a detached-JWS signature (PS256) on every call. Paste PEM contents; values are stored write-only and never displayed back.
            </p>
            {field("mTLS Client Certificate (PEM)", "tlsCertificate", "password", "-----BEGIN CERTIFICATE-----")}
            {field("mTLS Private Key (PEM)", "tlsPrivateKey", "password", "-----BEGIN PRIVATE KEY-----")}
            {field("JWS Signing Key (PEM, optional)", "signingPrivateKey", "password", "Defaults to the mTLS private key when empty")}
          </div>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, border: "1px solid #e5e7eb" }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700, color: GREEN }}>Integration Status</h3>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Active</label>
              <div
                onClick={() => setConfig(c => ({ ...c, isActive: !c.isActive }))}
                style={{
                  width: 44, height: 24, borderRadius: 12, cursor: "pointer",
                  background: config.isActive ? GOLD : "#d1d5db",
                  position: "relative", transition: "background 0.2s",
                }}
              >
                <div style={{
                  position: "absolute", top: 2, left: config.isActive ? 22 : 2,
                  width: 20, height: 20, borderRadius: "50%", background: "#fff",
                  transition: "left 0.2s",
                }} />
              </div>
              <span style={{ fontSize: 12, color: config.isActive ? "#065f46" : "#6b7280" }}>
                {config.isActive ? "Live" : "Disabled"}
              </span>
            </div>
            {testResult && (
              <div style={{
                padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontSize: 12,
                background: testResult.ok ? "#d1fae5" : "#fee2e2",
                color: testResult.ok ? "#065f46" : "#991b1b",
              }}>
                {testResult.ok ? "✓ " : "✗ "}{testResult.msg}
                {testResult.steps && testResult.steps.length > 0 && (
                  <ul style={{ margin: "8px 0 0", padding: 0, listStyle: "none" }}>
                    {testResult.steps.map((st, i) => (
                      <li key={i} style={{ marginTop: 6, color: st.ok ? "#065f46" : "#991b1b" }}>
                        <strong>{st.ok ? "✓" : "✗"} {st.label}:</strong> <span style={{ color: "#374151" }}>{st.msg}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <button
              onClick={testConn} disabled={testing}
              style={{
                width: "100%", padding: "10px 0", borderRadius: 8, border: `1px solid ${GREEN}`,
                background: "#fff", color: GREEN, fontWeight: 600, fontSize: 13, cursor: "pointer",
                marginBottom: 10,
              }}
            >
              {testing ? "Testing…" : "Test Connection"}
            </button>
          </div>
        </div>
      </div>
      <div style={{ marginTop: 20, textAlign: "right" }}>
        <button
          onClick={save} disabled={saving}
          style={{
            padding: "12px 32px", borderRadius: 8, border: "none",
            background: GOLD, color: GREEN, fontWeight: 700, fontSize: 14, cursor: "pointer",
          }}
        >
          {saving ? "Saving…" : "Save Configuration"}
        </button>
      </div>
    </div>
  );
};

// ─── Tab: Virtual IBANs ───────────────────────────────────────────────────────
const VibansTab = ({ token }) => {
  const [ibans, setIbans] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ userId: "", senderIban: "", senderBankName: "" });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = `page=${page}&limit=20${statusFilter ? `&status=${statusFilter}` : ""}`;
      const res = await getDataAPIAuth(`admin/alinma/vibans?${params}`, token);
      if (res.data.status === 1) {
        setIbans(res.data.data);
        setTotal(res.data.total);
      }
    } catch (e) {}
    setLoading(false);
  }, [token, page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const activate = async (id) => {
    if (!await dialog.confirm("Activate", "Activate this Virtual IBAN?")) return;
    try {
      await postDataAPI(`admin/alinma/vibans/${id}/activate`, {}, token);
      load();
    } catch (e) { dialog.alert("Error", e.response?.data?.msg || e.message); }
  };

  const deactivate = async (id) => {
    if (!await dialog.confirm("Deactivate", "Deactivate this Virtual IBAN?")) return;
    try {
      await postDataAPI(`admin/alinma/vibans/${id}/deactivate`, {}, token);
      load();
    } catch (e) { dialog.alert("Error", e.response?.data?.msg || e.message); }
  };

  const create = async () => {
    if (!form.userId || !form.senderIban) {
      dialog.alert("Error", "User ID and Sender IBAN are required");
      return;
    }
    setCreating(true);
    try {
      const res = await postDataAPI("admin/alinma/vibans", form, token);
      if (res.data.status === 1) {
        dialog.alert("Created", res.data.msg);
        setShowCreate(false);
        setForm({ userId: "", senderIban: "", senderBankName: "" });
        load();
      }
    } catch (e) { dialog.alert("Error", e.response?.data?.msg || e.message); }
    setCreating(false);
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8 }}>
          {["", "active", "pending", "inactive", "failed"].map(s => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              style={{
                padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                border: `1px solid ${statusFilter === s ? GREEN : "#d1d5db"}`,
                background: statusFilter === s ? GREEN : "#fff",
                color: statusFilter === s ? "#fff" : "#374151",
                cursor: "pointer",
              }}
            >
              {s || "All"}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            padding: "8px 20px", borderRadius: 8, border: "none",
            background: GOLD, color: GREEN, fontWeight: 700, fontSize: 13, cursor: "pointer",
          }}
        >
          + Create Virtual IBAN
        </button>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
        }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, width: 420, maxWidth: "90vw" }}>
            <h3 style={{ margin: "0 0 20px", color: GREEN }}>Create Virtual IBAN</h3>
            {[
              { label: "User ID (MongoDB _id)", key: "userId", placeholder: "64abc..." },
              { label: "Sender IBAN (whitelisted)", key: "senderIban", placeholder: "SA0380000000608010167519" },
              { label: "Sender Bank Name", key: "senderBankName", placeholder: "Alinma Bank" },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>{f.label}</label>
                <input
                  value={form[f.key]}
                  onChange={e => setForm(c => ({ ...c, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }}
                />
              </div>
            ))}
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowCreate(false)} style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" }}>Cancel</button>
              <button onClick={create} disabled={creating} style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: GOLD, color: GREEN, fontWeight: 700, cursor: "pointer" }}>
                {creating ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <p style={{ color: "#6b7280" }}>Loading…</p>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                {["Investor", "IBAN", "Sender IBAN", "Status", "Created", "Actions"].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#374151", borderBottom: "1px solid #e5e7eb" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ibans.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 32, textAlign: "center", color: "#9ca3af" }}>No virtual IBANs found</td></tr>
              ) : ibans.map(v => (
                <tr key={v._id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{v.userId?.name || "—"}</div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>{v.userId?.national_id}</div>
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 12, fontFamily: "monospace" }}>{v.iban || v.virtualAccountNumber || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 12, fontFamily: "monospace" }}>{v.senderIban || "—"}</td>
                  <td style={{ padding: "12px 16px" }}><StatusBadge status={v.status} /></td>
                  <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280" }}>{fmt(v.createdAt)}</td>
                  <td style={{ padding: "12px 16px" }}>
                    {v.status !== "active" && (
                      <button onClick={() => activate(v._id)} style={{ padding: "4px 12px", borderRadius: 6, border: "none", background: "#d1fae5", color: "#065f46", fontWeight: 600, fontSize: 11, cursor: "pointer", marginRight: 6 }}>Activate</button>
                    )}
                    {v.status === "active" && (
                      <button onClick={() => deactivate(v._id)} style={{ padding: "4px 12px", borderRadius: 6, border: "none", background: "#fee2e2", color: "#991b1b", fontWeight: 600, fontSize: 11, cursor: "pointer" }}>Deactivate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Pagination */}
          <div style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #e5e7eb" }}>
            <span style={{ fontSize: 12, color: "#6b7280" }}>Total: {total}</span>
            <div style={{ display: "flex", gap: 8 }}>
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", cursor: page === 1 ? "not-allowed" : "pointer", opacity: page === 1 ? 0.5 : 1 }}>‹ Prev</button>
              <span style={{ fontSize: 12, padding: "4px 8px" }}>Page {page}</span>
              <button disabled={ibans.length < 20} onClick={() => setPage(p => p + 1)} style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", cursor: ibans.length < 20 ? "not-allowed" : "pointer", opacity: ibans.length < 20 ? 0.5 : 1 }}>Next ›</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tab: Payment Events ──────────────────────────────────────────────────────
const EventsTab = ({ token }) => {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = `page=${page}&limit=20${statusFilter ? `&status=${statusFilter}` : ""}`;
      const res = await getDataAPIAuth(`admin/alinma/events?${params}`, token);
      if (res.data.status === 1) {
        setEvents(res.data.data);
        setTotal(res.data.total);
      }
    } catch (e) {}
    setLoading(false);
  }, [token, page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const credit = async (eventId, amount) => {
    if (!await dialog.confirm("Credit Wallet", `Credit investor wallet with ${SAR(amount)}?`)) return;
    try {
      const res = await postDataAPI(`admin/alinma/events/${eventId}/credit`, {}, token);
      if (res.data.status === 1) { dialog.alert("Credited", res.data.msg); load(); }
    } catch (e) { dialog.alert("Error", e.response?.data?.msg || e.message); }
  };

  const reject = async (eventId) => {
    if (!await dialog.confirm("Reject", "Mark this payment as rejected?")) return;
    try {
      await postDataAPI(`admin/alinma/events/${eventId}/reject`, { reason: "Rejected by admin" }, token);
      load();
    } catch (e) { dialog.alert("Error", e.response?.data?.msg || e.message); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {["", "received", "credited", "rejected"].map(s => (
          <button
            key={s}
            onClick={() => { setStatusFilter(s); setPage(1); }}
            style={{
              padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
              border: `1px solid ${statusFilter === s ? GREEN : "#d1d5db"}`,
              background: statusFilter === s ? GREEN : "#fff",
              color: statusFilter === s ? "#fff" : "#374151",
              cursor: "pointer",
            }}
          >
            {s || "All"}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={{ color: "#6b7280" }}>Loading…</p>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                {["Date", "Investor", "Amount", "Sender IBAN", "Receiver IBAN", "Status", "Actions"].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#374151", borderBottom: "1px solid #e5e7eb" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 32, textAlign: "center", color: "#9ca3af" }}>No payment events found</td></tr>
              ) : events.map(ev => (
                <tr key={ev._id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280" }}>{fmt(ev.paymentDate || ev.createdAt)}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{ev.userId?.name || ev.senderName || "—"}</div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>{ev.userId?.national_id}</div>
                  </td>
                  <td style={{ padding: "12px 16px", fontWeight: 700, color: GREEN }}>{SAR(ev.amount)}</td>
                  <td style={{ padding: "12px 16px", fontSize: 11, fontFamily: "monospace" }}>{ev.senderIban || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 11, fontFamily: "monospace" }}>{ev.receiverIban || "—"}</td>
                  <td style={{ padding: "12px 16px" }}><StatusBadge status={ev.status} /></td>
                  <td style={{ padding: "12px 16px" }}>
                    {ev.status === "received" && !ev.walletCredited && (
                      <>
                        <button onClick={() => credit(ev._id, ev.amount)} style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: "#d1fae5", color: "#065f46", fontWeight: 600, fontSize: 11, cursor: "pointer", marginRight: 6 }}>Credit</button>
                        <button onClick={() => reject(ev._id)} style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: "#fee2e2", color: "#991b1b", fontWeight: 600, fontSize: 11, cursor: "pointer" }}>Reject</button>
                      </>
                    )}
                    {ev.walletCredited && <span style={{ fontSize: 11, color: "#065f46" }}>✓ Credited</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #e5e7eb" }}>
            <span style={{ fontSize: 12, color: "#6b7280" }}>Total: {total}</span>
            <div style={{ display: "flex", gap: 8 }}>
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", cursor: page === 1 ? "not-allowed" : "pointer", opacity: page === 1 ? 0.5 : 1 }}>‹ Prev</button>
              <span style={{ fontSize: 12, padding: "4px 8px" }}>Page {page}</span>
              <button disabled={events.length < 20} onClick={() => setPage(p => p + 1)} style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", cursor: events.length < 20 ? "not-allowed" : "pointer", opacity: events.length < 20 ? 0.5 : 1 }}>Next ›</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tab: Stats ───────────────────────────────────────────────────────────────
const StatsTab = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDataAPIAuth("admin/alinma/stats", token)
      .then(res => { if (res.data.status === 1) setStats(res.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <p style={{ color: "#6b7280" }}>Loading stats…</p>;
  if (!stats) return <p style={{ color: "#ef4444" }}>Failed to load stats</p>;

  const cards = [
    { label: "Total Virtual IBANs", value: stats.virtualIbans.total, color: GREEN },
    { label: "Active IBANs", value: stats.virtualIbans.active, color: "#065f46" },
    { label: "Pending IBANs", value: stats.virtualIbans.pending, color: "#92400e" },
    { label: "Failed IBANs", value: stats.virtualIbans.failed, color: "#991b1b" },
    { label: "Total Payment Events", value: stats.paymentEvents.total, color: GREEN },
    { label: "Credited Events", value: stats.paymentEvents.credited, color: "#065f46" },
    { label: "Pending Events", value: stats.paymentEvents.pending, color: "#92400e" },
    { label: "Total Credited (SAR)", value: SAR(stats.paymentEvents.totalAmountCredited), color: GOLD, large: true },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16 }}>
      {cards.map(c => (
        <div key={c.label} style={{ background: "#fff", borderRadius: 12, padding: 20, border: "1px solid #e5e7eb" }}>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>{c.label}</div>
          <div style={{ fontSize: c.large ? 20 : 28, fontWeight: 800, color: c.color }}>{c.value}</div>
        </div>
      ))}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AlinmaAdmin = () => {
  const { auth } = useSelector(s => s);
  const token = auth?.data?.accesstoken || auth?.data?.access_token;
  const [activeTab, setActiveTab] = useState("config");

  const tabs = [
    { id: "config", label: "⚙️ Configuration" },
    { id: "vibans", label: "🏦 Virtual IBANs" },
    { id: "events", label: "💳 Payment Events" },
    { id: "stats", label: "📊 Statistics" },
  ];

  return (
    <AdminShell pageTitle="Alinma Bank — Virtual IBAN">
      <div style={{ padding: "24px 28px", maxWidth: 1200 }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: GREEN, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🏦</div>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: GREEN }}>Alinma Bank — Virtual IBAN</h1>
              <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>Manage Virtual IBANs for investor deposits and payment events</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "2px solid #e5e7eb" }}>
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                padding: "10px 20px", border: "none", background: "none", cursor: "pointer",
                fontSize: 13, fontWeight: 600,
                color: activeTab === t.id ? GREEN : "#6b7280",
                borderBottom: `2px solid ${activeTab === t.id ? GOLD : "transparent"}`,
                marginBottom: -2,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "config" && <ConfigTab token={token} />}
        {activeTab === "vibans" && <VibansTab token={token} />}
        {activeTab === "events" && <EventsTab token={token} />}
        {activeTab === "stats" && <StatsTab token={token} />}
      </div>
    </AdminShell>
  );
};

export default AlinmaAdmin;
